import { useEffect, useState } from 'react';
import { useUIStore } from '@/state/uiStore';
import { AudioManager } from '@/systems/AudioManager';
import { useSettingsStore } from '@/state/settingsStore';
import { useAdminStore } from '@/state/adminStore';
import { useCharacterStore } from '@/state/characterStore';
import { useSkillStore } from '@/state/skillStore';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

type TabKey = 'general' | 'auto';

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <span>{label}</span>
      <button
        style={value ? ui.primaryBtn : ui.secondaryBtn}
        onClick={() => onChange(!value)}
      >
        {value ? 'Bật' : 'Tắt'}
      </button>
    </div>
  );
}

function SliderRow({
  label, value, onChange, disabled,
}: { label: string; value: number; onChange: (v: number) => void; disabled?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ opacity: disabled ? 0.5 : 1 }}>{label}</span>
        <span style={{ color: '#9fc1ff' }}>{Math.round(value * 100)}%</span>
      </div>
      <input
        type="range" min={0} max={1} step={0.05}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        disabled={disabled}
        style={{ width: '100%' }}
      />
    </div>
  );
}

const SECTION: React.CSSProperties = {
  borderBottom: '1px solid #2c3e50',
  paddingBottom: 4,
  color: '#9fc1ff',
  fontSize: 12,
  textTransform: 'uppercase',
  marginTop: 6,
};

const TAB_BAR: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginBottom: 12,
  borderBottom: '1px solid #2c3e50',
};

function tabBtnStyle(active: boolean): React.CSSProperties {
  return {
    flex: 1,
    padding: '8px 10px',
    background: active ? 'linear-gradient(180deg,#2a3a52,#0f1520)' : 'transparent',
    color: active ? '#ffd166' : '#9fc1ff',
    border: '1px solid #2c3e50',
    borderBottom: active ? '1px solid #0f1520' : '1px solid transparent',
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 13,
    marginBottom: -1,
  };
}

export function SettingsPanel() {
  const close = () => useUIStore.getState().set('settings', false);

  const [tab, setTab] = useState<TabKey>('general');

  const [bgmMuted, setBgmMuted] = useState(AudioManager.isMuted());
  const [bgmVolume, setBgmVolume] = useState(AudioManager.getVolume());

  const showDamageNumbers = useSettingsStore((s) => s.showDamageNumbers);
  const showNameplates    = useSettingsStore((s) => s.showNameplates);
  const showFps           = useSettingsStore((s) => s.showFps);
  const sfxVolume         = useSettingsStore((s) => s.sfxVolume);
  const sfxMuted          = useSettingsStore((s) => s.sfxMuted);
  const setShowDamageNumbers = useSettingsStore((s) => s.setShowDamageNumbers);
  const setShowNameplates    = useSettingsStore((s) => s.setShowNameplates);
  const setShowFps           = useSettingsStore((s) => s.setShowFps);
  const setSfxVolume         = useSettingsStore((s) => s.setSfxVolume);
  const setSfxMuted          = useSettingsStore((s) => s.setSfxMuted);

  const autoUseHpPotion    = useSettingsStore((s) => s.autoUseHpPotion);
  const autoUseMpPotion    = useSettingsStore((s) => s.autoUseMpPotion);
  const hpPotionThreshold  = useSettingsStore((s) => s.hpPotionThreshold);
  const mpPotionThreshold  = useSettingsStore((s) => s.mpPotionThreshold);
  const autoCastSkill      = useSettingsStore((s) => s.autoCastSkill);
  const autoCastSkillIds   = useSettingsStore((s) => s.autoCastSkillIds);
  const autoPickupZen      = useSettingsStore((s) => s.autoPickupZen);
  const autoPickupEquip    = useSettingsStore((s) => s.autoPickupEquip);
  const autoWalkToLoot     = useSettingsStore((s) => s.autoWalkToLoot);
  const adminKeepSpacing   = useSettingsStore((s) => s.adminKeepSpacing);
  const setAutoUseHpPotion   = useSettingsStore((s) => s.setAutoUseHpPotion);
  const setAutoUseMpPotion   = useSettingsStore((s) => s.setAutoUseMpPotion);
  const setHpPotionThreshold = useSettingsStore((s) => s.setHpPotionThreshold);
  const setMpPotionThreshold = useSettingsStore((s) => s.setMpPotionThreshold);
  const setAutoCastSkill     = useSettingsStore((s) => s.setAutoCastSkill);
  const toggleAutoCastSkillId = useSettingsStore((s) => s.toggleAutoCastSkillId);
  const setAutoCastSkillIds   = useSettingsStore((s) => s.setAutoCastSkillIds);
  const setAutoPickupZen     = useSettingsStore((s) => s.setAutoPickupZen);
  const setAutoPickupEquip   = useSettingsStore((s) => s.setAutoPickupEquip);
  const setAutoWalkToLoot    = useSettingsStore((s) => s.setAutoWalkToLoot);
  const setAdminKeepSpacing  = useSettingsStore((s) => s.setAdminKeepSpacing);
  const isAdmin              = useAdminStore((s) => s.isAdmin);

  useEffect(() => {
    setBgmMuted(AudioManager.isMuted());
    setBgmVolume(AudioManager.getVolume());
  }, []);

  // Learned skills of the current class for the auto-cast checklist.
  const charClass = useCharacterStore((s) => s.current?.class);
  const charId    = useCharacterStore((s) => s.current?.id);
  const templates = useSkillStore((s) => s.templates);
  const learned   = useSkillStore((s) => s.learned);
  const fetchTemplates = useSkillStore((s) => s.fetch);
  const fetchLearned   = useSkillStore((s) => s.fetchLearned);

  useEffect(() => { fetchTemplates().catch(() => { /* swallow */ }); }, [fetchTemplates]);
  useEffect(() => {
    if (charId) fetchLearned(charId).catch(() => { /* swallow */ });
  }, [charId, fetchLearned]);

  const learnedSkills = charClass
    ? templates
        .filter((t) => t.class === charClass && (learned[t.id] ?? 0) > 0)
        .slice(0, 5)
    : [];

  const toggleBgmMute = () => {
    const next = !bgmMuted;
    AudioManager.setMuted(next);
    setBgmMuted(next);
  };

  const onBgmVolume = (v: number) => {
    AudioManager.setVolume(v);
    setBgmVolume(v);
  };

  const renderGeneral = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ ...SECTION, marginTop: 0 }}>Âm thanh</div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span>Nhạc nền (BGM)</span>
        <button style={bgmMuted ? ui.secondaryBtn : ui.primaryBtn} onClick={toggleBgmMute}>
          {bgmMuted ? 'Tắt' : 'Bật'}
        </button>
      </div>
      <SliderRow label="Âm lượng nhạc" value={bgmVolume} onChange={onBgmVolume} disabled={bgmMuted} />

      <ToggleRow label="Hiệu ứng âm thanh (SFX)" value={!sfxMuted} onChange={(v) => setSfxMuted(!v)} />
      <SliderRow label="Âm lượng SFX" value={sfxVolume} onChange={setSfxVolume} disabled={sfxMuted} />

      <div style={SECTION}>Hiển thị</div>

      <ToggleRow label="Hiện số sát thương" value={showDamageNumbers} onChange={setShowDamageNumbers} />
      <ToggleRow label="Hiện tên người chơi" value={showNameplates} onChange={setShowNameplates} />
      <ToggleRow label="Hiện chỉ báo FPS" value={showFps} onChange={setShowFps} />
    </div>
  );

  const renderAuto = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 11, color: '#7a8aa0', marginTop: 0 }}>
        Bật auto-attack bằng phím <b style={{ color: '#ffd166' }}>T</b>. Các tuỳ chọn dưới đây áp dụng khi auto-attack đang chạy.
      </div>

      <div style={{ ...SECTION, marginTop: 0 }}>Kỹ năng</div>

      <ToggleRow label="Tự cast kỹ năng (theo cooldown)" value={autoCastSkill} onChange={setAutoCastSkill} />

      {autoCastSkill && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#9fc1ff' }}>
              Chọn kỹ năng tự cast {autoCastSkillIds.length === 0 ? '(đang chọn: tất cả)' : `(${autoCastSkillIds.length})`}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                style={{ ...ui.secondaryBtn, fontSize: 10, padding: '2px 6px' }}
                onClick={() => setAutoCastSkillIds(learnedSkills.map((s) => s.id))}
                disabled={learnedSkills.length === 0}
              >Tất cả</button>
              <button
                style={{ ...ui.secondaryBtn, fontSize: 10, padding: '2px 6px' }}
                onClick={() => setAutoCastSkillIds([])}
              >Bỏ chọn</button>
            </div>
          </div>
          {learnedSkills.length === 0 ? (
            <div style={{ fontSize: 11, color: '#7a8aa0', fontStyle: 'italic' }}>
              Chưa học kỹ năng nào.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {learnedSkills.map((sk) => {
                const checked = autoCastSkillIds.length === 0 || autoCastSkillIds.includes(sk.id);
                const isAll = autoCastSkillIds.length === 0;
                return (
                  <label
                    key={sk.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: '2px 4px',
                      borderRadius: 3,
                      background: checked ? 'rgba(80,140,220,0.12)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        if (isAll) {
                          setAutoCastSkillIds(learnedSkills.filter((s) => s.id !== sk.id).map((s) => s.id));
                        } else {
                          toggleAutoCastSkillId(sk.id);
                        }
                      }}
                    />
                    <span>{sk.name}</span>
                    <span style={{ marginLeft: 'auto', color: '#7a8aa0', fontSize: 10 }}>
                      CD {Math.round(sk.cooldownMs / 100) / 10}s
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={SECTION}>Potion</div>

      <ToggleRow label="Auto dùng HP Potion" value={autoUseHpPotion} onChange={setAutoUseHpPotion} />
      <SliderRow
        label="Ngưỡng HP để uống potion"
        value={hpPotionThreshold}
        onChange={setHpPotionThreshold}
        disabled={!autoUseHpPotion}
      />

      <ToggleRow label="Auto dùng MP Potion" value={autoUseMpPotion} onChange={setAutoUseMpPotion} />
      <SliderRow
        label="Ngưỡng MP để uống potion"
        value={mpPotionThreshold}
        onChange={setMpPotionThreshold}
        disabled={!autoUseMpPotion}
      />

      <div style={SECTION}>Nhặt đồ</div>

      <ToggleRow label="Auto nhặt Zen (vàng)" value={autoPickupZen} onChange={setAutoPickupZen} />
      <ToggleRow label="Auto nhặt trang bị" value={autoPickupEquip} onChange={setAutoPickupEquip} />
      <ToggleRow label="Tự đi đến đồ rơi xa" value={autoWalkToLoot} onChange={setAutoWalkToLoot} />

      {isAdmin && (
        <>
          <div style={SECTION}>Admin</div>
          <ToggleRow
            label="Giữ khoảng cách với quái (kite)"
            value={adminKeepSpacing}
            onChange={setAdminKeepSpacing}
          />
          <div style={{ fontSize: 11, color: '#7a8aa0', marginTop: -8 }}>
            Khi auto-attack: lùi ra khỏi tầm đánh của quái nhưng vẫn nằm trong tầm đánh của bạn, để quái không thể chạm vào.
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      data-block-game-input="true"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        ...ui.panel,
        borderRadius: 0,
        zIndex: 50,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <strong style={{ fontSize: 16, color: '#ffd166' }}>Cài đặt</strong>
        <CloseButton onClick={close} />
      </div>

      <div style={TAB_BAR}>
        <button style={tabBtnStyle(tab === 'general')} onClick={() => setTab('general')}>
          Chung
        </button>
        <button style={tabBtnStyle(tab === 'auto')} onClick={() => setTab('auto')}>
          Tự động
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
        {tab === 'general' ? renderGeneral() : renderAuto()}
      </div>
    </div>
  );
}
